/**
 * YouTube channel for a granted client (read-only; flag social.youtube). ClientYouTube sits on the client blade's
 * summary tab: the client's channels with health and daily stats, connect / reconnect / disconnect (agency owner or
 * admin, client grant with write:content). Connect goes to Google, returns through the partners host's
 * /bff/oauth/connectors/callback to /integrations/connect (YouTubeReturnScreen), which finishes the connection and
 * offers the channel picker when the Google account has several channels. 404 (flag off, no grant) hides the panel.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { formatNumber } from '@qm/i18n';
import type { YouTubeAccountView, YouTubeChannelOption } from '@qm/contracts/social';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { BffError } from '../kit/http';
import { useSurface } from '../kit/context';
import { Badge, Notice, When } from '../kit/ui';
import { youtubeApi } from '../api/youtube';

const KNOWN = ['oauth_state_expired', 'missing_scopes', 'no_channels', 'channel_mismatch', 'channel_connected_elsewhere', 'provider_not_configured'] as const;
export function youtubeErrorKey(code: string | null | undefined): string {
  if (code === 'access_denied' || code === 'user_denied') return 'social.youtube.error.denied';
  return (KNOWN as readonly string[]).includes(code ?? '') ? `social.youtube.error.${code}` : 'social.youtube.error.generic';
}
const codeOf = (e: unknown) => (e instanceof BffError ? ((e.envelope?.error.details as { code?: string } | undefined)?.code ?? null) : null);
const safeUrl = (u: unknown) => { try { return typeof u === 'string' && new URL(u).protocol === 'https:' ? u : null; } catch { return null; } };

function useYouTubeApi() {
  const { http } = useSurface();
  return useMemo(() => youtubeApi(http), [http]);
}

export function ClientYouTube({ tenantId }: { tenantId: string }) {
  const { t, locale } = useSurface();
  const api = useYouTubeApi();
  const [accounts, setAccounts] = useState<YouTubeAccountView[] | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () => api.accounts(tenantId).then(setAccounts, (e: unknown) => {
    if (e instanceof BffError && (e.status === 404 || e.status === 403)) setHidden(true); else setError('social.youtube.error.generic');
  });
  useEffect(() => { void load(); }, [tenantId]);
  if (hidden) return null;
  const n = (v: number) => formatNumber(locale, v);

  const start = async (reconnectAccountId?: string) => {
    setBusy(true); setError(null);
    try {
      const url = safeUrl((await api.start(tenantId, reconnectAccountId)).authorizeUrl);
      if (!url) throw new Error('bad authorize url');
      window.location.assign(url);
    } catch (e) { setError(youtubeErrorKey(codeOf(e))); setBusy(false); }
  };
  const disconnect = async (id: string) => {
    setBusy(true); setError(null);
    try { await api.disconnect(tenantId, id); await load(); } catch (e) { setError(youtubeErrorKey(codeOf(e))); }
    setBusy(false);
  };

  return (
    <Panel>
      <div data-testid="client-youtube">
        <Heading level={3}>{t('social.youtube.title')}</Heading>
        <Text tone="muted">{t('social.youtube.intro')}</Text>
        <Text tone="muted">{t('social.youtube.readOnly')}</Text>
        {error ? <Notice tone="bad">{t(error)}</Notice> : null}
        {accounts && !accounts.length ? <Text>{t('social.youtube.empty')}</Text> : null}
        {accounts?.map((a) => (
          <div key={a.id} data-testid={`client-youtube-${a.channelId}`}>
            <Text><bdi>{a.displayName}</bdi> <Badge tone={a.action === 'reconnect' ? 'bad' : a.health === 'provider_issue' ? 'warn' : a.health === 'healthy' ? 'good' : 'neutral'}>{t(`social.youtube.health.${a.health}`)}</Badge></Text>
            {a.stats ? (
              <Text tone="muted">
                {t('social.youtube.stats.subscribers')}: {a.stats.subscribers === null ? t('social.youtube.stats.hidden') : n(a.stats.subscribers)}
                {' · '}{t('social.youtube.stats.views')}: {n(a.stats.views)}{' · '}{t('social.youtube.stats.videos')}: {n(a.stats.videos)}
                {' · '}<When iso={a.stats.fetchedAt} />
              </Text>
            ) : a.status === 'connected' ? <Text tone="muted">{t('social.youtube.stats.none')}</Text> : null}
            {a.action === 'reconnect' ? <Button onClick={() => void start(a.id)} disabled={busy}>{t('social.youtube.reconnect')}</Button> : null}
            {a.status === 'connected' ? <Button onClick={() => void disconnect(a.id)} disabled={busy}>{t('social.youtube.disconnect')}</Button> : null}
          </div>
        ))}
        <Button onClick={() => void start()} disabled={busy || accounts === null}>{t('social.youtube.connect')}</Button>
      </div>
    </Panel>
  );
}

/** Partners host /integrations/connect: the YouTube OAuth return (`yts_` states only). */
export function YouTubeReturnScreen() {
  const { t } = useSurface();
  const api = useYouTubeApi();
  const [phase, setPhase] = useState<'working' | 'choose' | 'error'>('working');
  const [errorKey, setErrorKey] = useState('social.youtube.error.generic');
  const [pending, setPending] = useState<{ id: string; tenantId: string; channels: YouTubeChannelOption[] } | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const started = useRef(false);
  const fail = (code: string | null | undefined) => { setErrorKey(youtubeErrorKey(code)); setPhase('error'); };
  const back = (tenantId: string) => window.location.assign(`/clients/${encodeURIComponent(tenantId)}?tab=summary`);

  useEffect(() => {
    if (typeof window === 'undefined' || started.current) return;
    started.current = true;
    const p = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    history.replaceState(null, '', window.location.pathname);
    const state = p.get('state') ?? '';
    const code = p.get('code') ?? '';
    if (p.get('error') || !/^yts_[A-Za-z0-9_-]{1,60}$/.test(state) || !code || code.length > 2048) { fail(p.get('error')); return; }
    api.callback(state, code).then((r) => {
      if (!r.pendingId) { back(r.tenantId); return; }
      setPending({ id: r.pendingId, tenantId: r.tenantId, channels: r.channels });
      setPhase('choose');
    }, (e: unknown) => fail(codeOf(e)));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!pending || !chosen.size) return;
    try { const r = await api.select(pending.id, [...chosen]); back(r.tenantId); } catch (err) { fail(codeOf(err)); }
  };
  const toggle = (id: string, on: boolean) => setChosen((s) => { const x = new Set(s); if (on) x.add(id); else x.delete(id); return x; });

  return (
    <section aria-labelledby="yt-return-title" data-testid="youtube-return">
      <Heading level={1} id="yt-return-title">{t('social.youtube.title')}</Heading>
      {phase === 'working' ? <Text role="status">{t('social.youtube.callback.working')}</Text> : null}
      {phase === 'error' ? (
        <div role="alert">
          <Notice tone="bad">{t(errorKey)}</Notice>
          <Button onClick={() => window.location.assign('/clients')}>{t('social.youtube.callback.back')}</Button>
        </div>
      ) : null}
      {phase === 'choose' && pending ? (
        <form onSubmit={(e) => void submit(e)}>
          <fieldset>
            <legend>{t('social.youtube.pick.title')}</legend>
            {pending.channels.map((c, i) => (
              <div key={c.channelId}>
                <input type="checkbox" id={`pyt-${i}`} checked={chosen.has(c.channelId)} onChange={(ev) => toggle(c.channelId, ev.target.checked)} />
                <label htmlFor={`pyt-${i}`}><bdi>{c.title}</bdi>{c.customUrl ? <> (<bdi>{c.customUrl}</bdi>)</> : null}</label>
              </div>
            ))}
          </fieldset>
          <Button type="submit" variant="primary" disabled={!chosen.size}>{t('social.youtube.pick.save')}</Button>
        </form>
      ) : null}
    </section>
  );
}
